import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";

import i18n from "../../config/i18n";
import { sharedApi, getApiRequestUrl } from "../../services/sharedApi";
import { clearCache } from "../../utils/cache/cache";
import {
  clearCampaignAttribution,
  getCampaignAttribution,
} from "../../utils/campaignAttribution";
import { toApiError } from "../../utils/errorUtils";
import { getUserAvatarUrlAsync } from "../../utils/gravatar";
import { logger } from "../../utils/logger";

import type { AuthResponse, UserProfile } from "@lingoshade/api";

interface LoginRequest {
  email: string;
  password: string;
}

// Unified registration interface (like in AuthContext)
export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  nativeLanguage: string;
  targetLanguage: string;
}

export interface AuthState {
  user: UserProfile | null;
  currentUserRequestId: string | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  isNewUser: boolean;
  // Avatar URL cache
  avatarUrl: string | null;
  // Legacy multi-step registration removed; flag kept only for potential onboarding logic
  registrationData: null; // deprecated placeholder
  // Anonymous user state
  isAnonymous: boolean;
  anonymousSession: {
    sessionId?: string;
    targetLanguage?: string;
    nativeLanguage?: string;
    level?: string; // CEFR level (BEGINNER, ELEMENTARY, INTERMEDIATE, UPPER_INTERMEDIATE, ADVANCED, PROFICIENT)
  } | null;
}

interface AuthErrorPayload {
  message: string;
  status?: number;
}

const initialState: AuthState = {
  currentUserRequestId: null,
  user: null,
  token: localStorage.getItem("token"),
  // Trust the stored token until proven invalid (401/403). This prevents redirect to /login
  // on F5 when the network is slow or the API has a transient error.
  isAuthenticated: (() => {
    const hasAnonymousSession =
      localStorage.getItem("hasAnonymousSession") === "true";
    const hasToken = !!localStorage.getItem("token");
    return hasToken && !hasAnonymousSession;
  })(),
  loading: false,
  error: null,
  isNewUser: false,
  avatarUrl: null, // Кеш URL аватара
  registrationData: null,
  isAnonymous: (() => {
    // Anonymous session is valid only if both flag and token exist
    const hasAnonymousSession =
      localStorage.getItem("hasAnonymousSession") === "true";
    const hasToken = !!localStorage.getItem("token");

    if (hasAnonymousSession && !hasToken) {
      // Cleanup invalid anonymous session
      localStorage.removeItem("hasAnonymousSession");
      localStorage.removeItem("anonymousSession");
      return false;
    }

    // If user has a real token, they're not anonymous
    // Check if token is an anonymous token or a real user token
    // For simplicity, if hasAnonymousSession is false but hasToken is true,
    // we assume it's a real user token and clear any leftover anonymous data
    if (!hasAnonymousSession && hasToken) {
      localStorage.removeItem("anonymousSession");
      return false;
    }

    return hasAnonymousSession;
  })(),
  anonymousSession: (() => {
    try {
      const hasAnonymousSession =
        localStorage.getItem("hasAnonymousSession") === "true";
      const saved = localStorage.getItem("anonymousSession");
      const hasToken = !!localStorage.getItem("token");

      // If not anonymous or no token, clear session
      if (!hasAnonymousSession || !hasToken) {
        if (saved) {
          localStorage.removeItem("anonymousSession");
        }
        return null;
      }

      if (!saved) return null;
      try {
        const parsed = JSON.parse(saved) as {
          sessionId?: string;
          targetLanguage?: string;
          nativeLanguage?: string;
          level?: string;
        };
        return {
          sessionId: parsed.sessionId,
          targetLanguage: parsed.targetLanguage,
          nativeLanguage: parsed.nativeLanguage,
          level: parsed.level, // may be undefined for legacy entries
        };
      } catch {
        // Corrupted JSON, clear it
        localStorage.removeItem("anonymousSession");
        return null;
      }
    } catch {
      return null;
    }
  })(),
};

// Login user
export const loginUser = createAsyncThunk<
  { user: UserProfile; token: string },
  LoginRequest,
  { rejectValue: string }
>("auth/login", async (credentials, { rejectWithValue }) => {
  try {
    logger.info("[auth] Password login started", {
      method: "POST",
      url: getApiRequestUrl("/auth/login"),
    });
    const response: AuthResponse = await sharedApi.auth.login(credentials);
    logger.info("[auth] Password login succeeded");
    return { user: response.user, token: response.token };
  } catch (error: unknown) {
    const failure = toApiError(error);
    logger.error("[auth] Password login failed", {
      method: "POST",
      url: getApiRequestUrl("/auth/login"),
      status: failure.status,
      code: failure.code,
    });
    const message =
      failure.code === "INVALID_CREDENTIALS"
        ? i18n.t("auth:password.incorrectPassword")
        : failure.code === "NETWORK_ERROR" || failure.code === "REQUEST_TIMEOUT"
          ? i18n.t("common:notifications.errors.networkError")
          : failure.status !== undefined && failure.status >= 500
            ? i18n.t("common:notifications.errors.unexpectedError")
            : failure.message || i18n.t("auth:errors.loginFailed");
    return rejectWithValue(message);
  }
});

// Create anonymous web session
export const createAnonymousSession = createAsyncThunk<
  { token: string; sessionId: string },
  { targetLanguage: string; nativeLanguage: string; level?: string },
  { rejectValue: string }
>("auth/createAnonymousSession", async (sessionData, { rejectWithValue }) => {
  try {
    const response = await sharedApi.auth.createWebSession(sessionData);
    return {
      token: response.token,
      sessionId: response.sessionId,
    };
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("auth:errors.failedToCreateAnonymousSession");
    return rejectWithValue(message);
  }
});

// Removed registerStep1/registerStep2 (deprecated multi-step flow)

// Get current user (equivalent to refreshUser from AuthContext)
export const getCurrentUser = createAsyncThunk<
  UserProfile,
  void,
  { rejectValue: AuthErrorPayload }
>("auth/getCurrentUser", async (_, { rejectWithValue }) => {
  try {
    // CRITICAL: Pass token from localStorage to authenticated endpoint
    const token = localStorage.getItem("token");
    logger.debug(
      "[authSlice] getCurrentUser: Token from localStorage:",
      token ? token.substring(0, 20) + "..." : "NONE",
    );
    if (!token) {
      logger.warn("[authSlice] getCurrentUser: No token found in localStorage");
      return rejectWithValue({
        message: i18n.t("common:interface.noTokenFound"),
      });
    }
    const response = await sharedApi.auth.me(token);
    logger.debug(
      "[authSlice] getCurrentUser: Success, got user:",
      response.user.email,
    );
    logger.debug(
      "[authSlice] getCurrentUser: Full user object:",
      response.user,
    );
    logger.debug(
      "[authSlice] getCurrentUser: nativeLanguage:",
      response.user.nativeLanguage,
    );
    logger.debug(
      "[authSlice] getCurrentUser: targetLanguage:",
      response.user.targetLanguage,
    );
    return response.user;
  } catch (error: unknown) {
    logger.error("[authSlice] getCurrentUser: Failed", error);
    const status =
      typeof error === "object" &&
      error !== null &&
      "status" in error &&
      typeof (error as { status?: unknown }).status === "number"
        ? (error as { status: number }).status
        : undefined;
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("auth:errors.failedToGetUser");
    return rejectWithValue({ message, status });
  }
});

// Load avatar URL and cache it
export const loadAvatarUrl = createAsyncThunk<
  string | null,
  { email: string; customAvatarUrl?: string | null; size?: number },
  { rejectValue: string }
>(
  "auth/loadAvatarUrl",
  async ({ email, customAvatarUrl, size = 40 }, { rejectWithValue }) => {
    try {
      const avatarUrl = await getUserAvatarUrlAsync(
        email,
        customAvatarUrl,
        size,
      );
      return avatarUrl;
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : i18n.t("auth:errors.failedToLoadAvatar");
      return rejectWithValue(message);
    }
  },
);

// Unified register (like in AuthContext) - combines both steps
export const register = createAsyncThunk<
  { user: UserProfile; token: string },
  RegisterRequest,
  { rejectValue: string }
>("auth/register", async (userData, { rejectWithValue }) => {
  try {
    const response = await sharedApi.auth.register({
      email: userData.email,
      password: userData.password,
      firstName: userData.firstName,
      lastName: userData.lastName,
      nativeLanguage: userData.nativeLanguage,
      targetLanguage: userData.targetLanguage,
      attribution: getCampaignAttribution(),
    });
    clearCampaignAttribution();
    return { user: response.user, token: response.token };
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : i18n.t("auth:errors.registrationFailed");
    return rejectWithValue(message);
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      // Очистка состояния аутентификации
      state.currentUserRequestId = null;
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isNewUser = false;
      state.registrationData = null;
      state.avatarUrl = null;
      state.loading = false;
      state.error = null;

      // Очистка анонимного состояния
      state.isAnonymous = false;
      state.anonymousSession = null;

      // Очистка токена из localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("hasAnonymousSession");
      localStorage.removeItem("anonymousSession");

      // ПОЛНАЯ ОЧИСТКА ВСЕХ ПОЛЬЗОВАТЕЛЬСКИХ ДАННЫХ
      // Получаем все ключи localStorage
      const keysToKeep = [
        "lingoshade_consent", // КРИТИЧНО: Согласие на cookies (GDPR)
        "lingoshade_device_id", // КРИТИЧНО: ID устройства для consent tracking
        "i18nextLng", // Язык интерфейса
        "dataVersion", // Версия данных приложения
      ];

      // Удаляем все ключи кроме системных
      Object.keys(localStorage).forEach((key) => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });

      // Очистка sessionStorage (полностью, так как это сессионные данные)
      sessionStorage.clear();

      // Очистка in-memory кэша
      clearCache();

      logger.debug("[authSlice] logout: All user data cleared");
    },
    clearError: (state) => {
      state.error = null;
    },
    clearNewUserFlag: (state) => {
      state.isNewUser = false;
    },
    setRegistrationData: () => {
      /* deprecated – no-op */
    },
    setRegistrationStep: () => {
      /* deprecated – no-op */
    },
    resetRegistration: (state) => {
      state.registrationData = null;
    },
    // Update anonymous session data (used texts counter)
    updateAnonymousSession: (
      state,
      action: PayloadAction<Partial<AuthState["anonymousSession"]>>,
    ) => {
      if (state.isAnonymous && state.anonymousSession) {
        state.anonymousSession = {
          ...state.anonymousSession,
          ...action.payload,
        };
        localStorage.setItem(
          "anonymousSession",
          JSON.stringify(state.anonymousSession),
        );

        // Mark anonymous session presence for restoration logic
        localStorage.setItem("hasAnonymousSession", "true");
      }
    },
    // Convert anonymous session to registered user
    convertAnonymousToUser: (
      state,
      action: PayloadAction<{ user: UserProfile; token: string }>,
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.isAnonymous = false;
      state.anonymousSession = null;
      localStorage.setItem("token", action.payload.token);
      localStorage.removeItem("anonymousSession");
    },
    // Sets authenticated user explicitly (used for restoration flows after redirects)
    setAuth: (
      state,
      action: PayloadAction<{ user: UserProfile; token: string }>,
    ) => {
      // Runtime defensive check (extra safety in case of unexpected shape during manual dispatch)
      const u = action.payload.user;
      if (
        !u ||
        typeof u !== "object" ||
        typeof u.id !== "string" ||
        typeof u.email !== "string"
      ) {
        // Ignore malformed user object – do not mutate state (could optionally log)
        return;
      }
      state.currentUserRequestId = null;
      state.loading = false;
      state.user = u;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.isAnonymous = false;
      state.anonymousSession = null;
      localStorage.setItem("token", action.payload.token);
      localStorage.removeItem("anonymousSession");
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        // CRITICAL: Очищаем данные предыдущего пользователя ПЕРЕД новым логином
        // Это гарантирует что новый пользователь не увидит старые данные
        logger.debug("[authSlice] loginUser.pending: Preparing for new login");
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        logger.debug(
          "[authSlice] loginUser.fulfilled: Saving token to localStorage",
        );
        logger.debug(
          "[authSlice] loginUser.fulfilled: User data:",
          action.payload.user,
        );
        logger.debug("[authSlice] loginUser.fulfilled: Languages:", {
          native: action.payload.user.nativeLanguage,
          target: action.payload.user.targetLanguage,
        });
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isAnonymous = false;
        state.anonymousSession = null;
        localStorage.setItem("token", action.payload.token);

        // Update UI language to match native language
        if (action.payload.user.nativeLanguage) {
          localStorage.setItem(
            "i18nextLng",
            action.payload.user.nativeLanguage,
          );
          logger.debug(
            "[authSlice] Updated i18nextLng to match native language:",
            action.payload.user.nativeLanguage,
          );
        }

        logger.debug(
          "[authSlice] loginUser.fulfilled: Token saved, user authenticated",
        );
        // Очищаем флаг анонимной сессии при авторизации
        localStorage.removeItem("hasAnonymousSession");
        localStorage.removeItem("anonymousSession");
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || i18n.t("auth:errors.loginFailed");
      });

    // Register (unified)
    builder
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isAnonymous = false;
        state.anonymousSession = null;
        state.isNewUser = true; // Set new user flag
        state.registrationData = null;
        localStorage.setItem("token", action.payload.token);
        // Очищаем флаг анонимной сессии при регистрации
        localStorage.removeItem("hasAnonymousSession");
        localStorage.removeItem("anonymousSession");
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || i18n.t("auth:errors.registrationFailed");
      });

    // Get current user
    builder
      .addCase(getCurrentUser.pending, (state, action) => {
        state.currentUserRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        if (state.currentUserRequestId !== action.meta.requestId) return;
        state.currentUserRequestId = null;
        logger.debug(
          "[authSlice] getCurrentUser.fulfilled: Payload received:",
          action.payload,
        );
        logger.debug("[authSlice] getCurrentUser.fulfilled: Languages:", {
          native: action.payload.nativeLanguage,
          target: action.payload.targetLanguage,
        });
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        if (state.currentUserRequestId !== action.meta.requestId) return;
        state.currentUserRequestId = null;
        state.loading = false;
        const status =
          action.payload?.status && typeof action.payload.status === "number"
            ? action.payload.status
            : undefined;
        const message =
          action.payload?.message ||
          action.error.message ||
          i18n.t("auth:errors.failedToGetUser");
        state.error = message;
        if (status === 401 || status === 403) {
          state.isAuthenticated = false;
          state.token = null;
          state.user = null;
          localStorage.removeItem("token");
        } else {
          logger.warn(
            "[authSlice] getCurrentUser.rejected: preserving token after recoverable error",
            {
              status,
              message,
            },
          );
        }
      });

    // Load avatar URL
    builder
      .addCase(loadAvatarUrl.fulfilled, (state, action) => {
        state.avatarUrl = action.payload;
      })
      .addCase(loadAvatarUrl.rejected, (state) => {
        state.avatarUrl = null;
      });

    // Anonymous session
    builder
      .addCase(createAnonymousSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAnonymousSession.fulfilled, (state, action) => {
        logger.debug(
          "[authSlice] createAnonymousSession.fulfilled - payload:",
          action.payload,
        );
        logger.debug(
          "[authSlice] createAnonymousSession.fulfilled - meta.arg:",
          action.meta.arg,
        );

        state.loading = false;
        state.token = action.payload.token;
        state.isAnonymous = true;
        state.isAuthenticated = false; // Anonymous users are not "authenticated"
        state.anonymousSession = {
          sessionId: action.payload.sessionId,
          targetLanguage: action.meta.arg.targetLanguage,
          nativeLanguage: action.meta.arg.nativeLanguage,
          level: action.meta.arg.level, // persist selected level for anonymous user
        };

        logger.debug(
          "[authSlice] About to save token to localStorage:",
          action.payload.token,
        );
        localStorage.setItem("token", action.payload.token);

        localStorage.setItem(
          "anonymousSession",
          JSON.stringify(state.anonymousSession),
        );

        // Update UI language to match native language
        if (action.meta.arg.nativeLanguage) {
          localStorage.setItem("i18nextLng", action.meta.arg.nativeLanguage);
          logger.debug(
            "[authSlice] Updated i18nextLng to match native language:",
            action.meta.arg.nativeLanguage,
          );
        }
      })
      .addCase(createAnonymousSession.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          i18n.t("auth:errors.failedToCreateAnonymousSession");
      });
  },
});

export const {
  logout,
  clearError,
  clearNewUserFlag,
  setRegistrationData,
  resetRegistration,
  setAuth,
  updateAnonymousSession,
  convertAnonymousToUser,
} = authSlice.actions;

export default authSlice.reducer;
